import React, { useState } from "react";
import { ethers } from "ethers";
import type { VaultInfo } from "../hooks/useVaults";
import { VAULT_ABI, ERC20_ABI, COLORS } from "../constants";

interface Props {
  vault: VaultInfo;
  signer: ethers.Signer | null;
  onRefresh: () => void;
}

type Tab = "deposit" | "withdraw";

export const VaultCard: React.FC<Props> = ({ vault, signer, onRefresh }) => {
  const [tab, setTab]         = useState<Tab>("deposit");
  const [amount, setAmount]   = useState("");
  const [status, setStatus]   = useState<{ text: string; ok?: boolean } | null>(null);
  const [busy, setBusy]       = useState(false);
  const [emergency, setEmerg] = useState(false);

  const perfPct     = (vault.perfFeeBps / 100).toFixed(1);
  const withdrawPct = (vault.withdrawFeeBps / 100).toFixed(2);
  const lastHarvest = vault.lastHarvest ? timeSince(vault.lastHarvest) : "Never";
  const hasPosition = parseFloat(vault.userShares) > 0;

  // Estimate LP out on withdraw (before fee)
  const estimatedLpOut = (() => {
    if (tab !== "withdraw" || !amount) return null;
    const shares   = parseFloat(amount);
    const total    = parseFloat(vault.userShares);
    if (!shares || !total) return null;
    const fraction = Math.min(shares / total, 1);
    const gross    = fraction * parseFloat(vault.userLpValue);
    const fee      = gross * vault.withdrawFeeBps / 10_000;
    return { gross: gross.toFixed(6), net: (gross - fee).toFixed(6), fee: fee.toFixed(6) };
  })();

  // Wallet providers (Rabby, some MetaMask versions) return nonce:"undefined"
  // in the RPC response which breaks ethers v6 ContractTransactionResponse parsing.
  // We bypass this by encoding calldata manually and sending via eth_sendRawTransaction,
  // then polling for the receipt ourselves using the hash.
  async function sendAndWait(
    contractAddress: string,
    abi: readonly string[],
    method: string,
    args: unknown[]
  ): Promise<void> {
    const iface = new ethers.Interface(abi);
    const data  = iface.encodeFunctionData(method, args);
    const from  = await signer!.getAddress();
    const provider = signer!.provider as ethers.BrowserProvider;

    const [gasEstimate, feeData] = await Promise.all([
      provider.estimateGas({ to: contractAddress, from, data }),
      provider.getFeeData(),
    ]);

    const txParams = {
      to:                   contractAddress,
      from,
      data,
      gas:                  ethers.toQuantity(gasEstimate * 120n / 100n), // 20% buffer
      maxFeePerGas:         ethers.toQuantity(feeData.maxFeePerGas ?? feeData.gasPrice ?? 0n),
      maxPriorityFeePerGas: ethers.toQuantity(feeData.maxPriorityFeePerGas ?? 0n),
      type:                 "0x2",
    };

    const hash = await provider.send("eth_sendTransaction", [txParams]) as string;
    setStatus({ text: "Confirming..." });

    // Poll for receipt
    for (let i = 0; i < 60; i++) {
      await new Promise(r => setTimeout(r, 2000));
      const receipt = await provider.send("eth_getTransactionReceipt", [hash]) as { status: string } | null;
      if (receipt) {
        if (receipt.status === "0x0") throw new Error("Transaction reverted on-chain");
        return;
      }
    }
    throw new Error("Transaction not confirmed after 2 minutes");
  }

  async function handleApprove() {
    if (!signer) return;
    setBusy(true);
    setStatus({ text: "Waiting for approval..." });
    try {
      await sendAndWait(vault.lpTokenAddress, ERC20_ABI, "approve", [vault.address, ethers.MaxUint256]);
      setStatus({ text: "Approved!", ok: true });
      onRefresh();
    } catch (e: unknown) {
      setStatus({ text: e instanceof Error ? e.message : "Approval failed" });
    } finally {
      setBusy(false);
    }
  }

  async function handleDeposit() {
    if (!signer || !amount) return;
    setBusy(true);
    setStatus({ text: "Submitting deposit..." });
    try {
      await sendAndWait(vault.address, VAULT_ABI, "deposit", [ethers.parseEther(amount)]);
      setStatus({ text: "Deposited! 🎉", ok: true });
      setAmount("");
      onRefresh();
    } catch (e: unknown) {
      setStatus({ text: e instanceof Error ? e.message : "Deposit failed" });
    } finally {
      setBusy(false);
    }
  }

  async function handleWithdraw() {
    if (!signer || !amount) return;
    setBusy(true);
    setStatus({ text: `Submitting ${emergency ? "emergency " : ""}withdrawal...` });
    try {
      const method = emergency ? "emergencyWithdraw" : "withdraw";
      await sendAndWait(vault.address, VAULT_ABI, method, [ethers.parseEther(amount)]);
      setStatus({ text: "Withdrawn! 🎉", ok: true });
      setAmount("");
      onRefresh();
    } catch (e: unknown) {
      setStatus({ text: e instanceof Error ? e.message : "Withdrawal failed" });
    } finally {
      setBusy(false);
    }
  }

  const needsApproval =
    tab === "deposit" &&
    parseFloat(amount || "0") > 0 &&
    parseFloat(vault.lpAllowance) < parseFloat(amount || "0");

  return (
    <div style={s.card}>
      {/* Header */}
      <div style={s.header}>
        <div>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <span style={s.vaultName}>{vault.name}</span>
            <span style={{
              ...s.poolBadge,
              background: vault.isStable ? "#0d2d1f" : "#1a1428",
              color:      vault.isStable ? "#4ade80" : "#a78bfa",
              border:     `1px solid ${vault.isStable ? "#166534" : "#6d28d9"}`,
            }}>
              {vault.isStable ? "Stable" : "Volatile"}
            </span>
          </div>
          <div style={s.vaultSub}>Aerodrome • Base</div>
        </div>
        <div style={s.aprBadge}>
          <span style={s.aprLabel}>Share Price</span>
          <span style={s.aprValue}>{fmt(vault.pricePerShare, 6)}</span>
        </div>
      </div>

      {/* Stats grid */}
      <div style={s.statsGrid}>
        <Stat label="Total LP"         value={`${fmt(vault.totalLpManaged, 4)} LP`} />
        <Stat label="Pending Rewards"  value={`${fmt(vault.pendingRewards, 4)} AERO`} highlight />
        <Stat label="Perf Fee"         value={`${perfPct}%`} />
        <Stat label="Withdraw Fee"     value={`${withdrawPct}%`} />
        <Stat label="Last Harvest"     value={lastHarvest} />
        <Stat label="Total Compounded" value={`${fmt(vault.totalRewardsCompounded, 2)} AERO`} />
      </div>

      {/* User position */}
      {hasPosition && (
        <div style={s.position}>
          <span style={s.posLabel}>Your position</span>
          <span style={s.posValue}>
            {fmt(vault.userShares, 6)} shares ≈ {fmt(vault.userLpValue, 6)} LP
          </span>
        </div>
      )}

      {/* Tabs */}
      <div style={s.tabs}>
        <button
          style={{ ...s.tab, ...(tab === "deposit"  ? s.tabActive : {}) }}
          onClick={() => { setTab("deposit");  setAmount(""); setStatus(null); }}
        >Deposit</button>
        <button
          style={{ ...s.tab, ...(tab === "withdraw" ? s.tabActive : {}) }}
          onClick={() => { setTab("withdraw"); setAmount(""); setStatus(null); }}
        >Withdraw</button>
      </div>

      {/* Input */}
      <div style={s.inputRow}>
        <input
          style={s.input}
          type="number"
          placeholder="0.0"
          value={amount}
          onChange={(e) => setAmount(e.target.value)}
          disabled={busy}
          min="0"
          step="any"
        />
        <button
          style={s.maxBtn}
          disabled={busy}
          onClick={() =>
            setAmount(tab === "deposit" ? vault.userLpBalance : vault.userShares)
          }
        >MAX</button>
      </div>

      {/* Balance hint */}
      <div style={s.balanceHint}>
        {tab === "deposit"
          ? `Wallet: ${fmt(vault.userLpBalance, 6)} LP`
          : `Shares: ${fmt(vault.userShares, 6)}`
        }
      </div>

      {/* Withdraw estimate */}
      {tab === "withdraw" && estimatedLpOut && (
        <div style={s.estimate}>
          <span>≈ {estimatedLpOut.net} LP</span>
          <span style={{ color: COLORS.textMuted }}>
            ({withdrawPct}% fee = {estimatedLpOut.fee} LP)
          </span>
        </div>
      )}

      {/* Emergency toggle */}
      {tab === "withdraw" && (
        <label style={s.checkLabel}>
          <input
            type="checkbox"
            checked={emergency}
            onChange={(e) => setEmerg(e.target.checked)}
            disabled={busy}
            style={{ marginRight: 6 }}
          />
          Emergency withdraw (no fee, works when paused)
        </label>
      )}

      {/* Action button */}
      {tab === "deposit" && needsApproval ? (
        <button style={{ ...s.btn, background: COLORS.yellow, color: "#000" }}
          onClick={handleApprove} disabled={busy || !signer}>
          {busy ? "Approving..." : "Approve LP Token"}
        </button>
      ) : tab === "deposit" ? (
        <button style={s.btn}
          onClick={handleDeposit}
          disabled={busy || !signer || !amount || parseFloat(amount) <= 0}>
          {busy ? "Depositing..." : "Deposit"}
        </button>
      ) : (
        <button
          style={{ ...s.btn, background: emergency ? COLORS.red : COLORS.primary }}
          onClick={handleWithdraw}
          disabled={busy || !signer || !amount || parseFloat(amount) <= 0}>
          {busy ? "Withdrawing..." : emergency ? "Emergency Withdraw" : "Withdraw"}
        </button>
      )}

      {/* Status */}
      {status && (
        <div style={{ ...s.status, color: status.ok ? COLORS.green : COLORS.red }}>
          {status.text}
        </div>
      )}
    </div>
  );
};

const Stat: React.FC<{ label: string; value: string; highlight?: boolean }> = ({
  label, value, highlight,
}) => (
  <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
    <div style={{ fontSize: 11, color: COLORS.textMuted, textTransform: "uppercase" as const, letterSpacing: "0.05em" }}>
      {label}
    </div>
    <div style={{ fontSize: 13, fontWeight: 600, color: highlight ? COLORS.green : COLORS.textPrimary }}>
      {value}
    </div>
  </div>
);

function fmt(val: string, decimals = 4): string {
  const n = parseFloat(val);
  if (isNaN(n) || n === 0) return "0";
  // For very small non-zero values, show enough decimals to be meaningful
  if (n > 0 && n < Math.pow(10, -decimals)) {
    const magnitude = Math.floor(Math.log10(n));
    const sigFigDecimals = Math.abs(magnitude) + 2;
    return n.toFixed(Math.min(sigFigDecimals, 12));
  }
  return n.toFixed(decimals);
}

function timeSince(date: Date): string {
  const s = Math.floor((Date.now() - date.getTime()) / 1000);
  if (s < 60)    return `${s}s ago`;
  if (s < 3600)  return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
}

const s: Record<string, React.CSSProperties> = {
  card: {
    background: COLORS.bgCard,
    border: `1px solid ${COLORS.border}`,
    borderRadius: 16,
    padding: 24,
    marginBottom: 16,
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    marginBottom: 20,
  },
  vaultName: { fontSize: 18, fontWeight: 800, color: COLORS.textPrimary },
  poolBadge: {
    display: "inline-block",
    fontSize: 10,
    fontWeight: 700,
    letterSpacing: "0.08em",
    textTransform: "uppercase" as const,
    padding: "2px 7px",
    borderRadius: 4,
    verticalAlign: "middle",
  },
  vaultSub:  { fontSize: 12, color: COLORS.textMuted, marginTop: 2 },
  aprBadge: {
    background: "#1e1e3f",
    borderRadius: 8,
    padding: "6px 12px",
    textAlign: "right" as const,
  },
  aprLabel: { display: "block", fontSize: 10, color: COLORS.textMuted, textTransform: "uppercase" as const },
  aprValue: { display: "block", fontSize: 16, fontWeight: 700, color: COLORS.accent },
  statsGrid: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr 1fr",
    gap: "12px 16px",
    marginBottom: 16,
  },
  position: {
    background: "#1a1a35",
    borderRadius: 8,
    padding: "8px 12px",
    marginBottom: 16,
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
  posLabel: { fontSize: 12, color: COLORS.textMuted },
  posValue: { fontSize: 13, fontWeight: 600, color: COLORS.accent },
  tabs: {
    display: "flex",
    gap: 4,
    marginBottom: 12,
    background: "#0d0d20",
    borderRadius: 8,
    padding: 4,
  },
  tab: {
    flex: 1,
    padding: "8px 0",
    borderRadius: 6,
    border: "none",
    background: "transparent",
    color: COLORS.textMuted,
    fontSize: 14,
    fontWeight: 600,
    cursor: "pointer",
  },
  tabActive: {
    background: COLORS.bgCard,
    color: COLORS.textPrimary,
  },
  inputRow: { display: "flex", gap: 8, marginBottom: 4 },
  input: {
    flex: 1,
    padding: "10px 12px",
    borderRadius: 8,
    border: `1px solid ${COLORS.border}`,
    background: COLORS.bgInput,
    color: COLORS.textPrimary,
    fontSize: 15,
    outline: "none",
  },
  maxBtn: {
    padding: "10px 14px",
    borderRadius: 8,
    border: "none",
    background: "#1e1e3f",
    color: COLORS.textSub,
    cursor: "pointer",
    fontSize: 12,
    fontWeight: 600,
  },
  balanceHint: { fontSize: 12, color: COLORS.textMuted, marginBottom: 8 },
  estimate: {
    fontSize: 12,
    color: COLORS.textSub,
    marginBottom: 8,
    display: "flex",
    gap: 8,
    flexWrap: "wrap" as const,
  },
  checkLabel: {
    display: "flex",
    alignItems: "center",
    fontSize: 12,
    color: COLORS.textMuted,
    marginBottom: 12,
    cursor: "pointer",
    userSelect: "none" as const,
  },
  btn: {
    width: "100%",
    padding: "13px 0",
    borderRadius: 8,
    border: "none",
    background: COLORS.primary,
    color: "#fff",
    fontSize: 15,
    fontWeight: 700,
    cursor: "pointer",
    marginTop: 4,
  },
  status: { marginTop: 10, fontSize: 13 },
};
